// PartyPanel — left-column slide-in panel that owns the party UI on the
// home screen. Mounts inside the home's scaled stage so it shares the
// same coord system as the menu it replaces; slides in from the menu's
// position with the same easing as CHANGING ROOM.
//
// Two visual states keyed off members.length:
//   solo (1)  → region picker + join-link + Find Match
//   party (≥2) → multi-avatar header + Leave Party + region picker + join-link + Find Match
//
// All state mutations come from the caller (OnlineFlowController) via
// setMembers / setJoinCode / setSelectedRegion / setRegionLocked /
// setMemberPing — the panel is presentational. User actions emit through
// the options.callbacks fields.

import { menuAudio } from '../MenuAudio';
import { MenuNav, type MenuItem } from '../MenuNav';
import { makeAvatarCircle } from '../components/AvatarCircle';
import { makeLevelBadge } from '../components/LevelBadge';
import { makePingBadge, type PingBadgeHandle } from '../components/PingBadge';
import { makeRegionPicker, type RegionPickerHandle } from './RegionPicker';
import type { HatId } from '../../render/HatModels';
import { t } from '../../i18n';

export interface PartyPanelMember {
  sessionId: string;
  name: string;
  color: string;
  hatId: HatId | null;
  level: number;
  isOwner: boolean;
  isLocal: boolean;
  /** Latest server-measured RTT in ms. 0 = no measurement yet. */
  ping?: number;
}

export interface PartyPanelCallbacks {
  onCopyJoinLink(): void;
  onFindMatch(): void;
  onLeaveParty(): void;
  /** User picked a different region from the dropdown. Caller is
   *  responsible for actually performing the migration; the panel will
   *  re-render the dropdown's "selected" state once the caller calls
   *  setSelectedRegion() with the post-migration id. */
  onSwitchRegion(regionId: string): void;
}

const STYLES_ID = 'party-panel-styles';
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Chewy&display=swap');

#party-panel {
  position: absolute;
  left: 80px; top: 80px;
  width: 560px;
  display: flex;
  flex-direction: column;
  font-family: 'Chewy', ui-sans-serif, system-ui, sans-serif;
  pointer-events: auto;
  opacity: 0;
  transform: translateX(-60px);
  transition: opacity 360ms cubic-bezier(0.65, 0, 0.35, 1) 80ms,
              transform 360ms cubic-bezier(0.65, 0, 0.35, 1) 80ms;
}
#party-panel.shown {
  opacity: 1;
  transform: translateX(0);
}
#party-panel.hiding {
  opacity: 0;
  transform: translateX(-60px);
  transition-delay: 0ms;
}

/* Header strip — avatars + name pins + ping badges. Sits above the panel
   body so the avatar circles bleed past the panel's top edge for that
   "stuck on top" feel. */
#party-panel .header {
  display: flex; align-items: flex-end; justify-content: center;
  gap: 18px;
  padding: 0 32px;
  margin-bottom: -52px;
  position: relative;
  z-index: 2;
  min-height: 140px;
}
#party-panel .header .slot {
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  pointer-events: none;
}
#party-panel .header .name-pin {
  background: #1E232C;
  color: #FFFFFF;
  font-size: 22px;
  padding: 4px 14px;
  border-radius: 8px;
  white-space: nowrap;
  max-width: 140px;
  overflow: hidden; text-overflow: ellipsis;
  box-shadow: 0 2px 4px rgba(0,0,0,0.25);
}
#party-panel .header .level-anchor {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}
#party-panel .header .level-anchor .level-badge {
  position: absolute;
  bottom: -6px;
}
#party-panel .header .ping-host {
  /* Sits below the name pin. PingBadge sets its own colors. */
  background: rgba(30, 35, 44, 0.55);
  border-radius: 8px;
  padding: 2px 8px;
  font-size: 14px;
  color: #FFFFFF;
  display: inline-flex;
  align-items: center;
  min-height: 20px;
}

/* Body card — the rounded blue panel containing the inputs/buttons. */
#party-panel .body {
  background: linear-gradient(180deg, #C7E6F1 0%, #B8DEEC 100%);
  border-radius: 28px;
  box-shadow: 0 16px 24px rgba(0,0,0,0.22), inset 0 -4px 6px rgba(89, 168, 192, 0.4);
  padding: 76px 36px 36px 36px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

/* Field rows — pills that contain inputs / buttons. */
#party-panel .field {
  background: #FFFFFF;
  border-radius: 18px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.18), inset 0 -3px 0 rgba(176, 209, 219, 0.6);
  padding: 14px 18px;
  display: flex; align-items: center; justify-content: space-between;
  gap: 14px;
  position: relative;
}
#party-panel .field .field-label {
  font-size: 18px;
  color: #6CABC1;
  letter-spacing: 0.04em;
  margin-bottom: 2px;
}
#party-panel .field .field-stack {
  display: flex; flex-direction: column;
  flex: 1; min-width: 0;
}
#party-panel .field .field-value {
  font-size: 24px;
  color: #0E3C4A;
  letter-spacing: 0.02em;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Side icon button (copy / leave-party). 60px square pill matching the
   home screen's dice button style. */
#party-panel .icon-btn {
  width: 60px; height: 60px;
  background: linear-gradient(180deg, #F1F9FB 0%, #C3D6DB 100%);
  box-shadow: 0 4px 0 #ADC5CC;
  border: 0;
  border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: transform 120ms ease, box-shadow 120ms ease, filter 120ms ease;
}
#party-panel .icon-btn img {
  width: 30px; height: 30px;
  object-fit: contain;
  display: block;
}
#party-panel .icon-btn:active { transform: translateY(3px); box-shadow: 0 1px 0 #ADC5CC; }
#party-panel .icon-btn:hover,
#party-panel .icon-btn.focused { filter: brightness(1.04); outline: 4px solid rgba(255,255,255,0.7); outline-offset: 3px; }

/* Leave Party row — stretches like a field but with the leave icon on
   the LEFT and label-style text. Uses the same field visual treatment. */
#party-panel .leave-party-row {
  background: #FFFFFF;
  border-radius: 18px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.18), inset 0 -3px 0 rgba(176, 209, 219, 0.6);
  padding: 14px 18px;
  display: flex; align-items: center; gap: 14px;
  cursor: pointer;
  border: 0;
  font-family: 'Chewy', sans-serif;
  font-size: 32px;
  color: #6CABC1;
  letter-spacing: 0.04em;
  text-align: left;
  width: 100%;
  transition: filter 120ms ease;
}
#party-panel .leave-party-row img {
  width: 36px; height: 36px;
  flex-shrink: 0;
}
#party-panel .leave-party-row:hover,
#party-panel .leave-party-row.focused { filter: brightness(1.04); outline: 4px solid rgba(255,255,255,0.7); outline-offset: 3px; }

/* Find Match button — primary call-to-action, blue gradient pill. */
#party-panel .find-match {
  background: linear-gradient(180deg, #6FCEEB 0%, #3DA6FF 100%);
  border: 0;
  border-radius: 22px;
  cursor: pointer;
  padding: 16px 24px;
  display: flex; align-items: center; justify-content: center;
  gap: 14px;
  font-family: 'Chewy', sans-serif;
  font-size: 44px;
  color: #FFFFFF;
  -webkit-text-stroke: 3px #2A6DA0;
  paint-order: stroke fill;
  box-shadow: 0 6px 0 #2C7AB6, 0 12px 22px rgba(0,0,0,0.25);
  letter-spacing: 0.04em;
  transition: transform 140ms ease, box-shadow 140ms ease, filter 140ms ease;
}
#party-panel .find-match img {
  width: 38px; height: 38px;
  object-fit: contain;
}
#party-panel .find-match:active { transform: translateY(3px); box-shadow: 0 3px 0 #2C7AB6, 0 8px 18px rgba(0,0,0,0.2); }
#party-panel .find-match:hover,
#party-panel .find-match.focused { filter: brightness(1.04); outline: 4px solid rgba(255,255,255,0.6); outline-offset: 4px; }
#party-panel .find-match[disabled] { filter: grayscale(0.3) brightness(0.92); cursor: default; }

/* Solo vs party mode: hide leave-party in solo. */
#party-panel.mode-solo .leave-party-row { display: none; }

/* Disabled-look for the Find Match button while:
   - the local user is not the party owner (only owners can search). */
#party-panel.find-match-disabled .find-match {
  filter: grayscale(0.45) brightness(0.85);
  cursor: default;
  box-shadow: 0 4px 0 #2C7AB6, 0 6px 12px rgba(0,0,0,0.18);
}
#party-panel.find-match-disabled .find-match:hover,
#party-panel.find-match-disabled .find-match.focused {
  filter: grayscale(0.45) brightness(0.85);
  outline: 0;
}
`;

export class PartyPanel {
  private root: HTMLElement;
  private nav: MenuNav | null = null;
  private members: PartyPanelMember[] = [];
  private joinLink = '';
  private mounted = false;
  private disposed = false;
  private mode: 'solo' | 'party' = 'solo';
  private copyConfirmTimer: ReturnType<typeof setTimeout> | null = null;
  private lastMembersFingerprint = '';
  private regionPicker: RegionPickerHandle | null = null;
  private selectedRegionId: string | null = null;
  private regionLocked = false;
  /** Per-member ping badges, keyed by sessionId. Disposed when the
   *  member leaves or the panel re-renders. */
  private memberBadges: Map<string, PingBadgeHandle> = new Map();

  constructor(
    private readonly host: HTMLElement,
    private readonly callbacks: PartyPanelCallbacks
  ) {
    ensureStyles();
    this.root = document.createElement('div');
    this.root.id = 'party-panel';
    this.root.classList.add('mode-solo');
    this.host.appendChild(this.root);
  }

  /** Mount the panel — append to host, render initial markup, slide it in
   *  via the .shown class. Idempotent. */
  mount(): void {
    if (this.mounted || this.disposed) return;
    this.mounted = true;
    this.render();
    requestAnimationFrame(() => {
      if (this.disposed) return;
      this.root.classList.add('shown');
    });
  }

  /** Tear down: animate out, then remove DOM and detach nav. */
  async dispose(): Promise<void> {
    if (this.disposed) return;
    this.disposed = true;
    this.nav?.dispose();
    this.nav = null;
    this.regionPicker?.dispose();
    this.regionPicker = null;
    for (const b of this.memberBadges.values()) b.dispose();
    this.memberBadges.clear();
    if (this.copyConfirmTimer) { clearTimeout(this.copyConfirmTimer); this.copyConfirmTimer = null; }
    this.root.classList.remove('shown');
    this.root.classList.add('hiding');
    await new Promise((r) => setTimeout(r, 300));
    this.root.remove();
  }

  setMembers(members: ReadonlyArray<PartyPanelMember>): void {
    if (this.disposed) return;
    // Skip the costly re-render if the structure (sessionIds + ownership
    // + identity) hasn't changed. updateFromState fires on every state
    // patch (search progress, vote tallies, …), most of which don't
    // touch members — re-rendering the whole DOM each time creates a
    // visible flicker as avatar canvases are torn down and re-created.
    // Note: ping is intentionally NOT in the fingerprint — pings update
    // independently via the ping badges, no DOM rebuild needed.
    const fingerprint = members
      .map((m) => `${m.sessionId}|${m.isOwner ? '1' : '0'}|${m.isLocal ? '1' : '0'}|${m.color}|${m.hatId ?? ''}|${m.level}|${m.name}`)
      .join('§');
    if (fingerprint === this.lastMembersFingerprint) {
      this.members = members.slice();
      this.updateMode();
      // Update ping badges with whatever new pings arrived.
      this.updateMemberPings();
      return;
    }
    this.lastMembersFingerprint = fingerprint;
    this.members = members.slice();
    this.updateMode();
    if (this.mounted) this.render();
  }

  /** Replace the displayed join link (full URL). Caller passes the URL
   *  it wants the copy button to write to clipboard. */
  setJoinLink(link: string): void {
    if (this.disposed) return;
    this.joinLink = link;
    if (this.mounted) {
      const codeEl = this.root.querySelector('.your-link-value') as HTMLElement | null;
      if (codeEl) codeEl.textContent = this.formatLinkForDisplay(link);
    }
  }

  /** Reflect the active region in the picker. Should be called by the
   *  caller after a successful migration / on initial mount. */
  setSelectedRegion(regionId: string | null): void {
    if (this.disposed) return;
    this.selectedRegionId = regionId;
    if (regionId) this.regionPicker?.setSelected(regionId);
  }

  setRegionLocked(locked: boolean): void {
    if (this.disposed) return;
    this.regionLocked = locked;
    this.regionPicker?.setLocked(locked);
  }

  /** Brief "Copied!" indicator on the copy button. Caller is responsible
   *  for the actual clipboard write. */
  flashCopyConfirm(): void {
    if (!this.mounted) return;
    const btn = this.root.querySelector('.copy-btn') as HTMLElement | null;
    if (!btn) return;
    btn.classList.add('copied');
    if (this.copyConfirmTimer) clearTimeout(this.copyConfirmTimer);
    this.copyConfirmTimer = setTimeout(() => btn.classList.remove('copied'), 1500);
  }

  /** Pause / resume input handling — used while a confirmation popup or
   *  the searching modal is on top. */
  setEnabled(enabled: boolean): void {
    this.nav?.setEnabled(enabled);
  }

  // -- internals ----------------------------------------------------------

  private updateMode(): void {
    const next = this.members.length >= 2 ? 'party' : 'solo';
    if (next !== this.mode) {
      this.mode = next;
      this.root.classList.remove('mode-solo', 'mode-party');
      this.root.classList.add(`mode-${next}`);
    }
    // Apply find-match-disabled class based on owner status. Solo players
    // are always their own owner so this only matters in 'party' mode.
    const local = this.members.find((m) => m.isLocal);
    const localIsOwner = local?.isOwner ?? true;
    this.root.classList.toggle('find-match-disabled', !localIsOwner);
  }

  private render(): void {
    if (!this.mounted) return;
    this.updateMode();
    // Header — avatars + ping
    const headerHtml = `
      <div class="header"></div>
      <div class="body">
        <div class="region-host"></div>
        <button type="button" class="leave-party-row" data-action="leave-party">
          <img src="/ui/onlineplay/leave-party-icon.svg" alt="" />
          <span>${t('party.leaveParty')}</span>
        </button>
        <div class="field your-link-row">
          <div class="field-stack">
            <div class="field-label">${t('party.yourJoinLink')}</div>
            <div class="field-value your-link-value">${escapeHtml(this.formatLinkForDisplay(this.joinLink))}</div>
          </div>
          <button type="button" class="icon-btn copy-btn" data-action="copy" title="${t('party.copyLink')}">
            <img src="/ui/onlineplay/copy-join-code-icon.svg" alt="" />
          </button>
        </div>
        <button type="button" class="find-match" data-action="find-match">
          <img src="/ui/onlineplay/find-match-icon.svg" alt="" />
          <span>${t('party.findMatch')}</span>
        </button>
      </div>
    `;
    this.root.innerHTML = headerHtml;
    this.populateHeader();
    // Re-build the region picker into the freshly-rendered host.
    this.regionPicker?.dispose();
    this.regionPicker = makeRegionPicker({
      onChange: (regionId) => this.callbacks.onSwitchRegion(regionId),
      // While the popover is open it owns keyboard / gamepad nav; pause
      // the panel's own MenuNav so arrow keys + Enter don't fight the
      // picker's internal nav.
      onOpenStateChange: (open) => this.nav?.setEnabled(!open)
    });
    if (this.selectedRegionId) this.regionPicker.setSelected(this.selectedRegionId);
    this.regionPicker.setLocked(this.regionLocked);
    const regionHost = this.root.querySelector('.region-host') as HTMLElement;
    regionHost.appendChild(this.regionPicker.el);
    this.installNav();
    this.updateMemberPings();
  }

  private populateHeader(): void {
    const header = this.root.querySelector('.header') as HTMLElement;
    header.innerHTML = '';
    // Wipe the previous round of badges — fresh DOM means fresh badges.
    for (const b of this.memberBadges.values()) b.dispose();
    this.memberBadges.clear();
    if (this.members.length === 0) {
      // No members yet (race during mount before setMembers fires) — leave
      // the header empty; populateHeader will be called again on the next
      // render trigger.
      return;
    }
    // Compute size based on member count: 1 → 96px, 2 → 86px, 3+ → 72px.
    const size = this.members.length >= 3 ? 72 : (this.members.length === 2 ? 86 : 96);
    for (const m of this.members) {
      const slot = document.createElement('div');
      slot.className = 'slot';
      const anchor = document.createElement('div');
      anchor.className = 'level-anchor';
      const avatar = makeAvatarCircle({
        color: m.color,
        hatId: m.hatId,
        size,
        withCrown: m.isOwner
      });
      anchor.appendChild(avatar);
      anchor.appendChild(makeLevelBadge({ level: m.level, fontSize: 16, height: 22 }));
      slot.appendChild(anchor);
      const pin = document.createElement('div');
      pin.className = 'name-pin';
      pin.textContent = m.isLocal ? t('party.you') : m.name;
      slot.appendChild(pin);
      // Ping badge — sits below the name pin. Live-updated from
      // setMembers calls (see updateMemberPings).
      const pingHost = document.createElement('div');
      pingHost.className = 'ping-host';
      const badge = makePingBadge({ size: 'sm' });
      badge.setPing(m.ping ?? 0);
      pingHost.appendChild(badge.el);
      slot.appendChild(pingHost);
      this.memberBadges.set(m.sessionId, badge);
      header.appendChild(slot);
    }
  }

  /** Push the latest ping value into each existing badge without rebuilding
   *  the DOM. Called every state patch. */
  private updateMemberPings(): void {
    for (const m of this.members) {
      const badge = this.memberBadges.get(m.sessionId);
      if (badge) badge.setPing(m.ping ?? 0);
    }
  }

  /** Trim a full URL to a short visual representation that fits inside
   *  the field-value pill. The clipboard payload is always the full URL. */
  private formatLinkForDisplay(link: string): string {
    if (!link) return '—';
    // Strip the protocol and any leading "www.".
    let s = link.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
    if (s.length > 36) s = s.slice(0, 33) + '…';
    return s;
  }

  private installNav(): void {
    this.nav?.dispose();
    const items: MenuItem[] = [];
    let row = 0;

    if (this.regionPicker) {
      items.push({
        el: this.regionPicker.el,
        row, col: 0,
        onActivate: () => {
          if (this.regionLocked) return;
          this.regionPicker?.setOpen(true);
        }
      });
      row += 1;
    }

    const leaveRow = this.root.querySelector('.leave-party-row') as HTMLElement | null;
    if (this.mode === 'party' && leaveRow) {
      items.push({
        el: leaveRow,
        row, col: 0,
        onActivate: () => this.callbacks.onLeaveParty()
      });
      row += 1;
    }

    const yourLinkRow = this.root.querySelector('.your-link-row') as HTMLElement | null;
    const copyBtn = this.root.querySelector('.copy-btn') as HTMLButtonElement | null;
    if (yourLinkRow && copyBtn) {
      items.push({
        el: yourLinkRow,
        row, col: 0,
        onActivate: () => copyBtn.click()
      });
      items.push({
        el: copyBtn,
        row, col: 1,
        onActivate: () => {
          this.callbacks.onCopyJoinLink();
          this.flashCopyConfirm();
        }
      });
      row += 1;
    }

    const findMatchBtn = this.root.querySelector('.find-match') as HTMLButtonElement | null;
    if (findMatchBtn) {
      items.push({
        el: findMatchBtn,
        row, col: 0,
        onActivate: () => {
          if (this.root.classList.contains('find-match-disabled')) return;
          menuAudio.playClick();
          this.callbacks.onFindMatch();
        }
      });
    }

    if (items.length === 0) return;
    this.nav = new MenuNav({
      items,
      initialIndex: items.length - 1, // default focus on Find Match
      // Controller B button = leave party / leave online. Mirrors the
      // existing Esc handler in OnlineFlowController, which routes via
      // a confirmation popup before tearing the panel down.
      onCancel: () => this.callbacks.onLeaveParty()
    });
    this.nav.mount();
  }
}

function ensureStyles(): void {
  if (document.getElementById(STYLES_ID)) return;
  const style = document.createElement('style');
  style.id = STYLES_ID;
  style.textContent = STYLES;
  document.head.appendChild(style);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
